import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  Bar,
  BarChart,
} from "recharts";

import { TIER_SCORE } from "../lib/api/dealer-quality";
import type { RiskTier } from "../lib/api/types";
import type { DealerCreditQuality, DealerVelocity } from "../lib/api/types";

const AXIS = { fill: "hsl(var(--muted-foreground))", fontSize: 11 };
const TOOLTIP = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "0px",
  color: "hsl(var(--popover-foreground))",
  fontSize: 12,
};

/** Diagnostic, not a verdict — one accent color for every dot and bar. */
const ACCENT = "var(--amber)";

const TIER_ORDER: RiskTier[] = ["A", "B1", "B2", "B3", "Other"];

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export interface DealerScatterPoint {
  dealerId: string;
  exceptionRatePct: number;
  modeRiskTier: RiskTier;
  riskTierScore: number;
  volumeCount: number;
  avgTicketSize: number;
}

export function buildDealerScatterPoints(
  creditQuality: DealerCreditQuality[],
  velocity: DealerVelocity[],
): DealerScatterPoint[] {
  const byId = new Map(velocity.map((row) => [row.dealerId, row]));

  return creditQuality.flatMap((dealer) => {
    const row = byId.get(dealer.dealerId);
    if (!row) return [];
    return [
      {
        dealerId: dealer.dealerId,
        exceptionRatePct: row.exceptionRatePct,
        modeRiskTier: dealer.modeRiskTier,
        riskTierScore: TIER_SCORE[dealer.modeRiskTier],
        volumeCount: dealer.volumeCount,
        avgTicketSize: row.avgTicketSize,
      },
    ];
  });
}

function ScatterTooltip({ active, payload }: { active?: boolean; payload?: unknown[] }) {
  if (!active || !payload?.length) return null;
  const point = (payload[0] as { payload: DealerScatterPoint }).payload;

  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg">
      <p className="font-semibold">{point.dealerId}</p>
      <dl className="mt-1 space-y-0.5">
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Exception rate</dt>
          <dd className="tabular-nums">{point.exceptionRatePct}%</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Mode risk tier</dt>
          <dd className="tabular-nums">{point.modeRiskTier}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Volume</dt>
          <dd className="tabular-nums">{point.volumeCount}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Avg ticket size</dt>
          <dd className="tabular-nums">{currency.format(point.avgTicketSize)}</dd>
        </div>
      </dl>
    </div>
  );
}

export function DealerRiskScatterChart({ points }: { points: DealerScatterPoint[] }) {
  const maxVolume = Math.max(1, ...points.map((point) => point.volumeCount));

  return (
    <ResponsiveContainer width="100%" height={340}>
      <ScatterChart margin={{ top: 12, right: 24, bottom: 28, left: 8 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
        <XAxis
          type="number"
          dataKey="exceptionRatePct"
          tick={AXIS}
          stroke="hsl(var(--border))"
          domain={[0, "dataMax + 4"]}
          tickFormatter={(value: number) => `${value}%`}
          label={{
            value: "Exception rate",
            position: "insideBottom",
            offset: -16,
            fill: "hsl(var(--muted-foreground))",
            fontSize: 11,
          }}
        />
        <YAxis
          type="number"
          dataKey="riskTierScore"
          tick={AXIS}
          stroke="hsl(var(--border))"
          domain={[0.5, 4.5]}
          ticks={[4, 3, 2, 1]}
          tickFormatter={(value: number) =>
            TIER_ORDER.find((tier) => TIER_SCORE[tier] === value) ?? ""
          }
          width={44}
          label={{
            value: "Mode risk tier",
            angle: -90,
            position: "insideLeft",
            fill: "hsl(var(--muted-foreground))",
            fontSize: 11,
          }}
        />
        <ZAxis type="number" dataKey="volumeCount" range={[60, 900]} domain={[0, maxVolume]} />
        <Tooltip content={<ScatterTooltip />} />
        <Scatter
          data={points}
          fill={ACCENT}
          fillOpacity={0.55}
          stroke={ACCENT}
          strokeOpacity={0.9}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

export function DealerExceptionRankChart({ rows }: { rows: DealerVelocity[] }) {
  const data = [...rows].sort((a, b) => b.exceptionRatePct - a.exceptionRatePct);

  return (
    <ResponsiveContainer width="100%" height={Math.max(260, data.length * 28)}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 32, bottom: 8, left: 8 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          tick={AXIS}
          stroke="hsl(var(--border))"
          tickFormatter={(value: number) => `${value}%`}
        />
        <YAxis type="category" dataKey="dealerId" tick={AXIS} stroke="hsl(var(--border))" width={72} />
        <Tooltip
          contentStyle={TOOLTIP}
          formatter={(value: number) => [`${value}%`, "Exception rate"]}
        />
        <Bar dataKey="exceptionRatePct" fill={ACCENT} radius={[0, 4, 4, 0]} maxBarSize={16} />
      </BarChart>
    </ResponsiveContainer>
  );
}
